import { Layers, Info, BarChart3 } from "lucide-react";
import {
  DetailRow,
  DetailGrid,
  SectionHeading,
  ComingSoonPlaceholder,
} from "@/components/dashboard/dashboardDetailPrimitives";
import { resolveSectorName } from "@/utils/sectorUtils";
import type { RiskAnalysisBySession, Sector } from "@/types/risk-analysis";

type EvaluareRiscDetailsProps = {
  riskData: RiskAnalysisBySession;
  sectors: Sector[];
  sessionId: string;
  onDetailsClick?: () => void;
};

function deriveHasModifications(riskData: RiskAnalysisBySession): boolean {
  return riskData.risk_analyses.some(
    (ra) => ra.has_populated_matrix && !ra.is_default
  );
}

function countJustifications(riskData: RiskAnalysisBySession): number {
  return riskData.risk_analyses.reduce((count, ra) => {
    const justCount = Object.values(ra.justifications ?? {}).filter(
      (v) => v === "da"
    ).length;
    return count + justCount;
  }, 0);
}

export default function EvaluareRiscDetails({
  riskData,
  sectors,
  sessionId,
  onDetailsClick,
}: EvaluareRiscDetailsProps) {
  if (riskData.risk_analyses.length === 0) {
    return (
      <ComingSoonPlaceholder
        title="Neinceput"
        message="Finalizati Formularul de Notificare pentru a incepe evaluarea de risc."
      />
    );
  }

  const hasModifications = deriveHasModifications(riskData);
  const justificationCount = countJustifications(riskData);

  return (
    <div className="space-y-2">
      <SectionHeading
        onClick={onDetailsClick}
        to={onDetailsClick ? undefined : `/analiza-risc/${sessionId}`}
      >
        Parametri Evaluare
      </SectionHeading>
      <DetailGrid>
        <DetailRow
          icon={Layers}
          label="Categoria cerinte"
          value={riskData.global_cyfun_level}
          highlighted
        />
        <DetailRow
          icon={Info}
          label="Modificari"
          value={hasModifications ? "DA" : "NU"}
        />
        <DetailRow
          icon={Info}
          label="Justificari"
          value={justificationCount > 0 ? `DA (${justificationCount})` : "NU"}
        />
        <DetailRow
          icon={BarChart3}
          label="Scor Maxim"
          value={String(riskData.global_score)}
          highlighted
        />
      </DetailGrid>

      {riskData.risk_analyses.length > 0 && (
        <>
          <SectionHeading>Sectoare Analizate</SectionHeading>
          <div className="flex flex-wrap gap-2">
            {riskData.risk_analyses.map((ra) => (
              <span
                key={ra.id}
                className="px-3 py-1.5 rounded-md bg-gradient-to-b from-blue-50 to-blue-100/50 border border-blue-200 text-xs font-semibold text-blue-800 shadow-sm"
              >
                {resolveSectorName(ra.sector_id, sectors)}
              </span>
            ))}
          </div>
        </>
      )}

      <SectionHeading>Cerinte Securitate Cibernetica</SectionHeading>
      <div className="flex items-center gap-3 p-3.5 rounded-lg bg-gradient-to-r from-blue-50/50 to-slate-50 border border-blue-100">
        <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
          <Layers size={14} className="text-blue-600" />
        </div>
        <div>
          <span className="text-[11px] text-slate-500 uppercase tracking-wide">
            Categorie aplicabila
          </span>
          <p className="text-sm font-bold text-blue-800">
            {riskData.global_cyfun_level}
          </p>
        </div>
      </div>
    </div>
  );
}
